import { vaultKeyringSchema, type SealedBlob, type VaultKey, type VaultKeyring } from '@curentia/shared';

const DB_NAME = 'curentia';
const DB_VERSION = 1;
const KEYRING = 'keyring';
const ITEMS = 'items';

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function finished(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/**
 * The device's storage in IndexedDB. Two stores: the keyring, which holds
 * only the login salt and the wrapped vault key, and items, each encrypted
 * under the vault key with its own context. Nothing readable is written, and
 * nothing goes to localStorage or sessionStorage.
 */
export class LocalStore {
  private constructor(private readonly db: IDBDatabase) {}

  static open(factory: IDBFactory = globalThis.indexedDB, name = DB_NAME): Promise<LocalStore> {
    return new Promise((resolve, reject) => {
      const request = factory.open(name, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(KEYRING)) db.createObjectStore(KEYRING);
        if (!db.objectStoreNames.contains(ITEMS)) db.createObjectStore(ITEMS);
      };
      request.onsuccess = () => resolve(new LocalStore(request.result));
      request.onerror = () => reject(request.error ?? new Error('Could not open storage'));
    });
  }

  async readKeyring(): Promise<VaultKeyring | null> {
    const value: unknown = await done(this.db.transaction(KEYRING).objectStore(KEYRING).get(KEYRING));
    const parsed = vaultKeyringSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
  }

  async writeKeyring(keyring: VaultKeyring): Promise<void> {
    const transaction = this.db.transaction(KEYRING, 'readwrite');
    transaction.objectStore(KEYRING).put(keyring, KEYRING);
    await finished(transaction);
  }

  /** Encrypts first, then writes, because an IndexedDB transaction closes while it waits for crypto. */
  async put(vaultKey: VaultKey, key: string, value: unknown): Promise<void> {
    const blob = await vaultKey.encryptJson(value, `item:${key}`);
    const transaction = this.db.transaction(ITEMS, 'readwrite');
    transaction.objectStore(ITEMS).put(blob, key);
    await finished(transaction);
  }

  async get<T>(vaultKey: VaultKey, key: string): Promise<T | undefined> {
    const blob = (await done(this.db.transaction(ITEMS).objectStore(ITEMS).get(key))) as SealedBlob | undefined;
    return blob ? vaultKey.decryptJson<T>(blob, `item:${key}`) : undefined;
  }

  async keys(): Promise<string[]> {
    const keys = await done(this.db.transaction(ITEMS).objectStore(ITEMS).getAllKeys());
    return keys.map(String);
  }

  async remove(key: string): Promise<void> {
    const transaction = this.db.transaction(ITEMS, 'readwrite');
    transaction.objectStore(ITEMS).delete(key);
    await finished(transaction);
  }

  /** Deletes everything this device stored. */
  async wipe(): Promise<void> {
    const transaction = this.db.transaction([KEYRING, ITEMS], 'readwrite');
    transaction.objectStore(KEYRING).clear();
    transaction.objectStore(ITEMS).clear();
    await finished(transaction);
  }

  close(): void {
    this.db.close();
  }
}
